import { useState } from 'react';
import type { ServiceCode } from '../../../common/enums/serviceCode';
import { UrlBuilder } from '../../../common/utils/urlBuilder';
import { fetchJson } from '../../store/fetch';
import { translateKey } from '../../translate';
import {
    AdminTable,
    Button,
    DeleteButton,
    DropdownSearch,
    FormGroup,
    FormLabel,
    InputGroup,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from './common';

interface IAdminServicesSelectorProps {
    onChange: (selectedServices: IService[]) => void;
    selectedServices: IService[];
}

export interface IService {
    code: ServiceCode;
    id: string;
}

export function AdminServicesSelector(props: IAdminServicesSelectorProps) {
    const [selectedService, setSelectedService] = useState<IService | null>(null);

    return (
        <>
            {props.selectedServices.length === 0 ? (
                translateKey('service.noServicesSelected')
            ) : (
                <AdminTable>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translateKey('service.service')}</TableHeader>
                            <TableHeader></TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {props.selectedServices.map((service) => (
                            <TableRow key={service.id}>
                                <TableData>{service.code}</TableData>
                                <TableData align="right">
                                    <DeleteButton onClick={async () => await removeService(service)} />
                                </TableData>
                            </TableRow>
                        ))}
                    </TableBody>
                </AdminTable>
            )}
            <FormGroup>
                <FormLabel>{translateKey('service.addService')}</FormLabel>
                <InputGroup>
                    <DropdownSearch
                        loadOptions={async (input) => await loadServiceOptions(input)}
                        onChange={async (value) => setSelectedService(value)}
                        value={selectedService}
                    />
                    <Button
                        color="secondary"
                        disabled={selectedService === null}
                        onClick={async () => await addService()}
                    >
                        {translateKey('common.add')}
                    </Button>
                </InputGroup>
            </FormGroup>
        </>
    );

    async function addService() {
        if (selectedService === null || props.selectedServices.some((service) => service.id === selectedService.id)) {
            return;
        }
        const selectedServices = [...props.selectedServices, selectedService];
        await props.onChange(selectedServices);
    }

    async function removeService(service: IService) {
        const selectedServices = props.selectedServices.filter((svc) => svc.id !== service.id);
        await props.onChange(selectedServices);
    }

    async function loadServiceOptions(input: string) {
        const urlBuilder = new UrlBuilder('/api/services');

        if (input.length > 0) {
            urlBuilder.add('name', input);
        }

        const url = urlBuilder.add('serviceIsActive', true).build();
        const result = await fetchJson(url, 'GET');
        const options = result.json.items.map((service: IService) => ({ value: service, label: service.code }));

        return options;
    }
}
